import React from 'react';
import { chrome } from '../reportTheme';

interface ScoreRingProps {
  /** 0-100, or undefined when the KPI was not computable. */
  value?: number;
  color: string;
  size?: number;
  thickness?: number;
}

/**
 * A score as a ring, the way the Cockpit and Dashboard mockups show them
 * (spec §17). Plain SVG - a single arc needs no charting library.
 *
 * A value that is not computable draws the empty track and an em dash, never a
 * full-looking ring at zero (spec §3.3).
 */
const ScoreRing: React.FC<ScoreRingProps> = ({ value, color, size = 56, thickness = 5 }) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : null;

  return (
    <svg width={size} height={size} role="img">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={chrome.grid}
        strokeWidth={thickness}
      />
      {pct !== null && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${(circumference * pct) / 100} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      )}
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        style={{
          fontFamily: "'Onest', system-ui, sans-serif",
          fontWeight: 600,
          fontSize: size <= 48 ? 13 : 15,
          fill: chrome.textPrimary,
        }}
      >
        {pct !== null ? `${Math.round(pct)}%` : '—'}
      </text>
    </svg>
  );
};

export default ScoreRing;
